import { Show } from "@refinedev/antd";
import { useCan, useNotification, useOne } from "@refinedev/core";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Card, Descriptions, Image, Input, Space, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { Link, useParams } from "react-router";

import { api } from "../api";
import { DATE_TIME_FORMAT, QUESTION_REPORT_REASONS, REPORT_STATUS } from "../labels";
import type { QuestionReportDetail } from "../types";

/**
 * /question-reports/:id (permission `questions.moderate`): the student's question, the tutor's
 * answer (image, voice, text) and the decision note. File links last a few minutes; reload the
 * page for new ones.
 */
export function QuestionReportShowPage() {
  const { id = "" } = useParams();
  const { open } = useNotification();
  const queryClient = useQueryClient();
  const { result: report, query } = useOne<QuestionReportDetail>({
    resource: "question-reports",
    id,
  });
  const { data: canSeeStudents } = useCan({ resource: "students", action: "list" });
  const { data: canSeeTutors } = useCan({ resource: "tutors", action: "list" });
  const [resolution, setResolution] = useState("");
  const [busy, setBusy] = useState(false);

  async function resolve() {
    setBusy(true);
    try {
      await api(`/api/office/question-reports/${encodeURIComponent(id)}/resolve`, {
        method: "POST",
        body: { resolution: resolution.trim() },
      });
      open?.({ type: "success", message: "Bildirim sonuçlandırıldı." });
      await Promise.all([
        query.refetch(),
        queryClient.invalidateQueries({ queryKey: ["office", "badges"] }),
      ]);
    } catch (error) {
      open?.({ type: "error", message: error instanceof Error ? error.message : "Hata oluştu." });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Show
      title="Soru çözümü bildirimi"
      isLoading={query.isLoading}
      canEdit={false}
      canDelete={false}
    >
      {report ? (
        <Space direction="vertical" size={24} style={{ width: "100%" }}>
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Ders">{report.subjectName}</Descriptions.Item>
            <Descriptions.Item label="Öğrenci">
              {canSeeStudents?.can ? (
                <Link to={`/students/${report.student.id}`}>{report.student.name}</Link>
              ) : (
                report.student.name
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Eğitmen">
              {report.tutor && canSeeTutors?.can ? (
                <Link to={`/tutors/${report.tutor.id}`}>{report.tutor.name}</Link>
              ) : (
                (report.tutor?.name ?? "—")
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Neden">
              {QUESTION_REPORT_REASONS[report.reason]}
              {report.note ? ` · ${report.note}` : ""}
            </Descriptions.Item>
            <Descriptions.Item label="Tarih">
              {dayjs(report.createdAt).format(DATE_TIME_FORMAT)}
            </Descriptions.Item>
            <Descriptions.Item label="Durum">
              <Tag color={REPORT_STATUS[report.status]?.color}>
                {REPORT_STATUS[report.status]?.label}
              </Tag>
              {report.resolution
                ? ` ${report.resolution} (${report.resolvedBy ?? "—"}, ${dayjs(report.resolvedAt).format(DATE_TIME_FORMAT)})`
                : ""}
            </Descriptions.Item>
          </Descriptions>

          <Card size="small" title="Soru">
            <Space direction="vertical">
              {report.question.imageUrl ? (
                <Image src={report.question.imageUrl} alt="Soru" width={360} />
              ) : (
                <Typography.Text type="secondary">Fotoğraf silinmiş.</Typography.Text>
              )}
              {report.question.note ? (
                <Typography.Paragraph style={{ whiteSpace: "pre-wrap" }}>
                  Not: {report.question.note}
                </Typography.Paragraph>
              ) : null}
            </Space>
          </Card>

          <Card size="small" title="Çözüm">
            <Space direction="vertical" style={{ width: "100%" }}>
              {report.answer.imageUrl ? (
                <Image src={report.answer.imageUrl} alt="Çözüm" width={480} />
              ) : null}
              {report.answer.audioUrl ? (
                <audio
                  controls
                  src={report.answer.audioUrl}
                  style={{ width: "100%", maxWidth: 480 }}
                />
              ) : null}
              {report.answer.text ? (
                <Typography.Paragraph style={{ whiteSpace: "pre-wrap" }}>
                  {report.answer.text}
                </Typography.Paragraph>
              ) : null}
            </Space>
          </Card>

          {report.status === "OPEN" ? (
            <Card size="small" title="Sonuçlandır">
              <Space direction="vertical" style={{ width: "100%" }}>
                <Input.TextArea
                  aria-label="Sonuç"
                  placeholder="Ne yapıldı? (ör. eğitmen uyarıldı, öğrenciye yazıldı)"
                  maxLength={500}
                  rows={3}
                  value={resolution}
                  onChange={(event) => setResolution(event.target.value)}
                />
                <Button
                  type="primary"
                  loading={busy}
                  disabled={!resolution.trim()}
                  onClick={() => void resolve()}
                >
                  Sonuçlandır
                </Button>
              </Space>
            </Card>
          ) : null}
        </Space>
      ) : null}
    </Show>
  );
}
